import React, { FC, useCallback, useEffect, useState } from 'react';
import { DifferentiationsOverview } from './DifferentiationsOverview';
import { NewDifferentiation } from './NewDifferentiation';
import { DifferentiationScreenAction } from './DifferentiationScreenAction';
import { UpdateDifferentiation } from './UpdateDifferentiation';
import { UpdateDifferentiationVisibility } from './UpdateDifferentiationVisibility';
import { DeleteTrajectoryVersion } from './DeleteTrajectoryVersion';
import {
  ITrajectoryDetail,
  ITrajectoryDifferentiation,
} from '../../../../config/api/models/Trajectory';
import { useFetch, useUser } from '../../../shared/hooks';
import apiRoutes from '../../../../config/api/routes';
import { useAppDispatch, useAppSelector } from 'src/modules/shared/hooks/useAppDispatch';
import { isSchoolHours } from 'src/modules/shared/helpers/isSchoolHours';
import DemoDialog, { ModalProps } from 'src/modules/app/components/Demo/DemoDialog';
import { setHasSeenDifferentiation } from 'src/modules/shared/slices/ctas/CtasSlice';
import { LogActionTypes } from 'src/modules/shared/data-logging';
import { showHelpModals } from 'src/modules/shared/constants';

type IdentifierProps = { bookId: string; trajectoryId: string; chapterId: string };
type Props = {
  bookId: string;
  chapterId: string;
  trajectory: ITrajectoryDetail;
  onChangeScreen: (isOverview: boolean) => void;
};

export type ScreenProps = {
  openCallback: (screen: DifferentiationScreenAction, meta?: any) => void;
  metaData: { version?: ITrajectoryDifferentiation };
} & IdentifierProps;

const getPreferredVersion = (
  trajectoryId: string,
  differentiations: ITrajectoryDifferentiation[]
): string => {
  let result = trajectoryId;

  let i;
  for (i = 0; i < differentiations.length; i++) {
    if (differentiations[i].isPreferred) {
      result = differentiations[i].id;
      break;
    }
  }

  return result;
};

const env = process.env.REACT_APP_ENVIRONMENT;

export const DifferentiationsContainer: FC<Props> = ({
  trajectory,
  onChangeScreen,
  bookId,
  chapterId,
}) => {
  const dispatch = useAppDispatch();
  const [metaData, setMetaData] = useState({});
  const { isTeacher } = useUser();
  const hasSeenDifferentiation = useAppSelector((state) => state.ctasSlice.hasSeenDifferentiation);
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [trajectoryVersionScreen, switchTrajectoryVersionScreen] = useState(
    DifferentiationScreenAction.OVERVIEW
  );

  const differentiationsModalProps: ModalProps = {
    title: 'Zet de lesmethode naar jouw hand',
    hasSeen: () => dispatch(setHasSeenDifferentiation(true)),
    body: [
      'Leerstof weglaten of toevoegen? Dat kan!',
      'Eenvoudigere of moeilijkere versies maken? Geen probleem.',
      <>
        Zelfs <strong>eigen presentaties of Bookwidgets</strong> sluit je met gemak in.
      </>,
    ],
    videoUrl: 'https://vimeo.com/912503801?share=copy',
    checkboxLabel: 'Verberg deze melding',
    buttonText: 'SLUIT',
    log: LogActionTypes.DIFFERENTIATIONS_SEEN_CTA,
  };

  const [getDifferentiationsResponse, getDifferentiations] =
    useFetch<ITrajectoryDifferentiation[]>('');

  const doGetDifferentiationsFetch = useCallback(() => {
    getDifferentiations({
      path: apiRoutes.differentiations(trajectory.parentId ?? trajectory.id),
    });
  }, [getDifferentiations, trajectory]);

  useEffect(() => {
    if (!bookId || !chapterId) return;
    doGetDifferentiationsFetch();
  }, [bookId, chapterId, trajectory, getDifferentiations, doGetDifferentiationsFetch]);

  const handleCallback = useCallback(
    (screen: DifferentiationScreenAction, meta?: any) => {
      setMetaData(meta || {});
      switchTrajectoryVersionScreen(screen);

      if (screen === DifferentiationScreenAction.OVERVIEW) {
        if (meta === 'REFETCH') doGetDifferentiationsFetch();
        onChangeScreen(true);
      } else {
        onChangeScreen(false);
      }
    },
    [setMetaData, switchTrajectoryVersionScreen, onChangeScreen, doGetDifferentiationsFetch]
  );

  const screenProps: ScreenProps = {
    openCallback: handleCallback,
    metaData,
    trajectoryId: trajectory.id,
    bookId,
    chapterId,
  };

  const preferredDifferentiation = getPreferredVersion(
    trajectory.parentId ?? trajectory.id,
    getDifferentiationsResponse.success ? getDifferentiationsResponse.data : []
  );

  useEffect(() => {
    setShowDialog(
      !hasSeenDifferentiation &&
        (env === 'demo' || (env !== 'demo' && !isSchoolHours() && isTeacher)) &&
        showHelpModals
    );
  }, [hasSeenDifferentiation, isTeacher]);

  return getDifferentiationsResponse.success && preferredDifferentiation ? (
    <>
      {
        {
          [DifferentiationScreenAction.OVERVIEW]: (
            <DifferentiationsOverview
              openCallback={handleCallback}
              metaData={metaData}
              bookId={bookId}
              chapterId={chapterId}
              trajectoryId={trajectory.parentId ?? trajectory.id}
              currentVersionId={trajectory.id}
              differentiations={getDifferentiationsResponse.data}
              onUpdate={doGetDifferentiationsFetch}
              preferredDifferentiation={preferredDifferentiation}
            />
          ),
          [DifferentiationScreenAction.CREATE]: (
            <NewDifferentiation
              openCallback={handleCallback}
              metaData={metaData}
              trajectoryId={trajectory.parentId ?? trajectory.id}
              bookId={bookId}
              chapterId={chapterId}
            />
          ),
          [DifferentiationScreenAction.EDIT]: <UpdateDifferentiation {...screenProps} />,
          [DifferentiationScreenAction.VISIBILITY]: (
            <UpdateDifferentiationVisibility
              {...screenProps}
              differentiations={getDifferentiationsResponse.data}
            />
          ),
          [DifferentiationScreenAction.DELETE]: <DeleteTrajectoryVersion {...screenProps} />,
        }[trajectoryVersionScreen]
      }
      {isTeacher && (
        <DemoDialog
          showDialog={showDialog}
          onClose={() => setShowDialog(false)}
          props={differentiationsModalProps}
        />
      )}
    </>
  ) : null;
};
